import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import { Validators, FormGroup, AbstractControl, ValidationErrors, FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../auth.service';
import { LoginModel } from './login.model';
import { SocialAuthService } from 'angularx-social-login';
import { GoogleLoginProvider } from 'angularx-social-login';
import { DashboardService } from 'src/app/dashboard.service';
import { Channel } from 'src/app/public/signup/signup.component';
import { AppleLoginProvider } from 'src/app/shared/services/sign-in-with-apple.service';
import {emailRegex, phoneRegex} from '../../consts/regex-patterns.consts';
import {HttpErrorResponse, HttpStatusCode} from '@angular/common/http';
import {Subscription} from 'rxjs';
import {UserIntegrityService} from '../../user-integrity.service';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})

export class LoginComponent implements OnInit, OnDestroy {
    channel: Channel;
    hide = true;

    countryCode = '52';
    loginForm: FormGroup;

    // login subscriptions
    private subs: Subscription[] = [];

    public error: string;
    public isMobile: boolean;

    @Output() userNeedsRecovery = new EventEmitter();

    constructor(
        private router: Router,
        private authService: AuthService,
        private fb: FormBuilder,
        public dashboard: DashboardService,
        private socialAuthService: SocialAuthService,
        private userIntegrityService: UserIntegrityService,
    ) {}

    ngOnInit(): void {
        this.isMobile = this.dashboard.isMobile;

        this.setupLoginForm();
        this.observeAccountPattern();

        this.dashboard.isMobile$.subscribe((value) => {
            this.isMobile = value;
        });
    }

    ngOnDestroy() {
        this.subs.forEach(sub => {
            sub.unsubscribe();
        });
    }

    private setupLoginForm(): void {
        this.loginForm = this.fb.group({
            pais: ['52', [
                Validators.nullValidator,
            ]],
            account: ['', [
                Validators.required,
                this.accountValidator.bind(this),
                ]
            ],
            password: ['', [
                Validators.required,
                Validators.minLength(8),
                Validators.maxLength(24)
                ]
            ]});
    }

    private observeAccountPattern(): void {
        this.subs.push(this.loginForm.get('account').valueChanges.subscribe(
            newValue => {
                const trimmedValue = newValue.trim();

                if (trimmedValue === '') {
                    this.channel = null;
                    this.loginForm.get('account').setErrors({ required: true });
                    return;
                }

                if (phoneRegex.test(trimmedValue)) {
                    this.channel = Channel.WhatsApp;
                }

                if (emailRegex.test(trimmedValue)) {
                    this.channel = Channel.Email;
                }
            }
        ));
    }

    private accountValidator(control: AbstractControl): ValidationErrors | null {
        const trimmedValue = control.value.trim();

        if (this.channel === Channel.SMS && !phoneRegex.test(trimmedValue)) {
            return { invalidPhone: true };
        }

        if (this.channel === Channel.Email && !emailRegex.test(trimmedValue)) {
            return { invalidEmail: true };
        }

        return null;
    }

    passwordVisibility(): void {
        this.hide = !this.hide;
    }

    signInWithGoogle(): void {
        this.dashboard.spin$.next(true);
        this.socialAuthService.signIn(GoogleLoginProvider.PROVIDER_ID).then((socialUser) => {
            this.authService.loginWithGoogle(socialUser).subscribe(
                (response) => this.onLoginResponse(response)
            );
        }).catch(() => {
            this.dashboard.spin$.next(false);
        });
    }

    signInWithApple(): void {
        this.dashboard.spin$.next(true);
        this.socialAuthService.signIn(AppleLoginProvider.PROVIDER_ID).then((socialUser) => {
            this.authService.loginWithApple(socialUser).subscribe(
                (response) => this.onLoginResponse(response)
            );
        }).catch(() => {
            this.dashboard.spin$.next(false);
        });
    }

    private saveUserDataInStorage(response: any) {
        const { id_usu, multiempresa, empresa, tipologin, sw_req_pass, email, id_benef, nombre_login } = response;
        const loginType = (tipologin) ? tipologin : 't';
        const reqPass = (sw_req_pass) ? sw_req_pass.toString() : '0';
        const name = (nombre_login && nombre_login !== '') ? nombre_login.split(' ') : null;

        if (name) {
            localStorage.setItem('name', name[0]);
        }

        localStorage.setItem('user_id', id_usu);
        localStorage.setItem('user_enterprise_id', empresa);
        localStorage.setItem('user_multi_enterprise', multiempresa);
        localStorage.setItem('user_account', email);
        localStorage.setItem('cw_sess_st', 'ok');
        localStorage.setItem('logged_beneficiary_id', `${id_benef}`);
        localStorage.setItem('userType', loginType.trim().toLocaleLowerCase());
        localStorage.setItem('require_pwd_in_link', reqPass);
    }

    public login(): void {
        this.requestLogin();
    }

    private requestLogin() {
        const loginData: LoginModel = Object.assign({}, this.loginForm.value);

        if (this.loginForm.invalid) {
            const controls = this.loginForm.controls;
            Object.keys(controls).forEach((value, index, data) => {
                controls[value].markAsTouched({ onlySelf: true });
            });

            return;
        }

        if (phoneRegex.test(loginData.account)) {
            loginData.account = `${this.countryCode}${loginData.account}`;
        }

        this.subs.push(this.authService.login(loginData).subscribe(
            (response) => this.onLoginResponse(response),
            (err: HttpErrorResponse) => {
                const { status } = err;

                if (status === HttpStatusCode.Unauthorized) {
                    this.loginForm.setErrors({ invalidCredentials: true });
                }
            }
        ));
    }

    public onLoginResponse(response: any): void {
        const { data } = response;
        const { codigo } = data;

        if (codigo === 0) {
            this.loginForm.setErrors(null);
            this.saveUserDataInStorage(data);
            this.userIntegrityService.checkNow();
            this.router.navigate(['/app/dashboard/cards']);
        }
    }

    public requestRecovery(): void {
        this.userNeedsRecovery.emit();
    }
}
